import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { ICompany } from 'types/company';
import useResource from 'util/resource/useResource';
import Resources from 'util/resource/Resources';
import { companiesAPIUrls } from 'apis/CompanyAPI/companies/companiesApi';
import { PostReach } from 'apis/CompanyAPI/insights/types';
import SectionHeading from 'ui/elements/SectionHeading';
import StatsCard from 'ui/views/cards/StatsCard';
import Card from 'ui/views/cards/Card';
import Button from 'ui/elements/buttons/Button';
import { bluePlanetTheme } from 'ui/theme';
import { contentSpacing } from 'ui/theme/themeConstants';
import { companyUrls } from 'urls';
import { formatThousands } from 'util/numbers';
import { pluralize } from 'util/stringUtils';

interface Props {
  company: ICompany;
  range: { startDate: Date; endDate: Date };
}

const Grid = styled.div`
  display: grid;
  gap: ${contentSpacing};
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  ${bluePlanetTheme.breakpoints.down('lg')} {
    grid-template-columns: 1fr 1fr 1fr;
  }
  ${bluePlanetTheme.breakpoints.down('md')} {
    grid-template-columns: 1fr 1fr;
  }
`;

export default function PostInsights({ company, range }: Props) {
  const { resource: postReach } = useResource<PostReach>(
    companiesAPIUrls.insights.postReach(company.id, range.startDate, range.endDate),
  );

  return (
    <>
      <SectionHeading heading="Posts" className="u-section-spacing-top" addMobileMargin />
      <Resources resources={[postReach]}>
        {([{ reach, newAudience, postsInPeriod, opens, engaged }]) => {
          if (postsInPeriod === 0 && reach.value === 0) {
            return (
              <Card>
                <p className="text-medium">You haven&apos;t posted in this period.</p>
                <p className="text-small u-half-spacing-top">
                  Companies get more investor views the week after they post.
                </p>
                <Link to={companyUrls.overview(company.slug, 'updates', { isPostingUpdate: true })}>
                  <Button className="u-content-spacing-top" kind="primary">
                    Post update
                  </Button>
                </Link>
              </Card>
            );
          }

          return (
            <>
              <p className="text-medium u-content-spacing-bottom">
                {postsInPeriod > 0 && (
                  <>
                    You published {postsInPeriod} {pluralize(postsInPeriod, 'post')} in this period.{' '}
                  </>
                )}
                Your posts were seen by {formatThousands(reach.value)} {pluralize(reach.value, 'person', 'people')}
                {newAudience.value > 0 && <>, {formatThousands(newAudience.value)} of them new to you</>}.{' '}
                {formatThousands(opens.value)} opened one and {formatThousands(engaged.value)} liked or commented.
              </p>
              <Grid>
                <StatsCard title="Posts" value={postsInPeriod} tooltip="Posts you published in this period" />
                <StatsCard
                  title="Reached"
                  value={reach.value}
                  previousValue={reach.previousValue}
                  tooltip="People who saw your posts: in their feed, on your profile, on company cards, or by opening them. Your own team isn't counted."
                />
                <StatsCard
                  title="New to you"
                  value={newAudience.value}
                  previousValue={newAudience.previousValue}
                  tooltip="People who saw your posts and don't follow you, aren't connected to your company and haven't added it to a collection"
                />
                <StatsCard
                  title="Opened"
                  value={opens.value}
                  previousValue={opens.previousValue}
                  tooltip="People who opened one of your posts to read it"
                />
                <StatsCard
                  title="Liked or commented"
                  value={engaged.value}
                  previousValue={engaged.previousValue}
                  tooltip="People who liked or commented on your posts"
                />
              </Grid>
            </>
          );
        }}
      </Resources>
    </>
  );
}
